import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import useConfirmationModal from "../../Components/Utils/DeleteModal";
import { useToast } from "../../Hooks/useToast";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import MainHeader from "../../Components/Headres/Main_Header";
import DatagridTable from "../../Components/Table/DataGrid_Table";
import { createColumn } from "../../Components/Table/DataGrid_Cullums";
import CellImg from "../../Components/Table/Cell_Img";

import DeleteIcon from "../../Assets/Icons/Delete.svg";
import EditIcon from "./../../Assets/Icons/Edit.svg";

import { fetchGetAllPackagesApi } from "../../Redux/Package_AutoComplete";
import { deletePackeges } from "../../Apis/Package/Package";

const SearchSchema = Yup.object().shape({
  name: Yup.string(),
  price: Yup.number().typeError("Price must be a number"),
  duration: Yup.number().typeError("Duration must be a number")
});

const PackagesTable = () => {
  const navigate = useNavigate();
  const { toastSuccess, toastError } = useToast();
  const dispatch = useDispatch();
  const role = localStorage.getItem("role");

  const { data, isLoading, error, pagination } = useSelector(
    (state) => state.PackegesAutocomplete
  );

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });

  const [searchFilters, setSearchFilters] = useState({});

  useEffect(() => {
    dispatch(
      fetchGetAllPackagesApi({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        filters: searchFilters
      })
    );
  }, [paginationModel.page, paginationModel.pageSize, searchFilters]);

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const { openModal: openDeleteModal, ModalComponent } = useConfirmationModal();

  const handleDelete = async (id) => {
    try {
      const res = await deletePackeges(id);
      if (res.status === 200 || res.ok) {
        toastSuccess("Das Paket wurde erfolgreich gelöscht.");
        dispatch(
          fetchGetAllPackagesApi({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            filters: searchFilters
          })
        );
      }
    } catch (error) {
      console.error("Delete error:", error);
      toastError("Fehler beim Löschen des Pakets. Bitte versuchen Sie es erneut.");
    }
  };

  const handleSearch = (values) => {
    const filters = {};
    if (values.name) filters.name = values.name;
    if (values.price) filters.price = values.price;
    if (values.duration) filters.duration = values.duration;

    setSearchFilters(filters);
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const handleReset = (resetForm) => {
    resetForm();
    setSearchFilters({});
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const columns = [
    createColumn("name", "Paketname", 250),
    createColumn("photo", "Bild", 250, "actions", (params) => (
      <CellImg src={params.value} />
    )),
    createColumn("duration", "Dauer", 250, "string", (params) =>
      params.value != null ? `${params.value} min` : ""
    ),
    createColumn("price", "Preis", 250),
    createColumn("priceInDayOff", "Preis am freien Tag", 250),
    createColumn("numberOfServices", "Anzahl der Leistungen", 250),
    ...(role === "manager"
      ? [
          createColumn("aktion", "Aktion", 200, "actions", (params) => (
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                color="primary"
                size="small"
                onClick={() => navigate(`Updatepackage/${params.row.id}`)}
              >
                <img style={{ height: "30px" }} src={EditIcon} alt="Edit" />
              </IconButton>
              <IconButton
                onClick={() => openDeleteModal(() => handleDelete(params.row.id))}
                color="error"
                size="small"
              >
                <img style={{ height: "30px" }} src={DeleteIcon} alt="delete" />
              </IconButton>
            </Box>
          ))
        ]
      : [])
  ];

  const Toolbar = () => (
    <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, bgcolor: "#D6B981" }}>
      <Formik
        initialValues={{ name: "", price: "", duration: "" }}
        validationSchema={SearchSchema}
        onSubmit={handleSearch}
      >
        {({ errors, touched, resetForm, values }) => (
          <Form
            style={{
              justifyContent: "end",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              width: "100%"
            }}
          >
            <Field
              as={TextField}
              name="name"
              label="Name"
              variant="outlined"
              size="small"
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Field
              as={TextField}
              name="price"
              label="Preis"
              variant="outlined"
              size="small"
              error={touched.price && Boolean(errors.price)}
              helperText={touched.price && errors.price}
            />
            <Field
              as={TextField}
              name="duration"
              label="Dauer (min)"
              variant="outlined"
              size="small"
              error={touched.duration && Boolean(errors.duration)}
              helperText={touched.duration && errors.duration}
            />
            <Button type="submit" variant="contained" color="inherit" size="medium">
              suchen
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              size="medium"
              onClick={() => handleReset(resetForm)}
            >
              Aktualisieren
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );

  return (
    <Box>
      <MainHeader onNavigate={() => navigate("AddEditPakete")} title="Neues Paket" />
      <Box sx={{ height: 600, width: "100%", mt: 3 }}>
        <DatagridTable
          columns={columns}
          rows={data || []}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={pagination?.total || 0}
          pageSizeOptions={[5, 10, 25]}
          loading={isLoading}
          toolbarComponent={<Toolbar />}
        />
      </Box>
      <ModalComponent />
    </Box>
  );
};

export default PackagesTable;